import React, { FC, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Button, Form, RadioButton, RadioGroup, Textfield } from "@natera/form";
import { FormField, Notification, Progress } from "@app/components";
import { useErrorController } from "@natera/platform/lib/hooks";
import { ErrorProvider, usePatientEducation } from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { routes } from "@app/routing";
import {
  SUBMIT_PATIENT_EDUCATION_FORM,
  SubmitPatientEducationData,
  SubmitPatientEducationVariables,
  BiologicalSex,
  InterestResponse,
} from "@app/service/patientEducation";
import { usePatientEducationHeap } from "@app/hooks/usePatientEducationHeap";
import { PatientEducationLoader } from "./PatientEducationLoader";
import { DobInput } from "@app/components/forms/dobInput";

import "./patientEducation.scss";

const messages = defineMessages({
  title: {
    id: "patientEducation.questionnaire.title",
    defaultMessage: "Thanks for watching the videos!",
  },
  stepIndicator: {
    id: "patientEducation.questionnaire.stepIndicator",
    defaultMessage: "Step 3 of 3",
  },
  description: {
    id: "patientEducation.questionnaire.description",
    defaultMessage:
      "Please complete the questionnaire below. Your responses will be shared with your provider at your next appointment.",
  },
  yourInformation: {
    id: "patientEducation.questionnaire.yourInformation",
    defaultMessage: "Your Information",
  },
  firstName: {
    id: "patientEducation.questionnaire.firstName",
    defaultMessage: "First Name",
  },
  firstNamePlaceholder: {
    id: "patientEducation.questionnaire.firstNamePlaceholder",
    defaultMessage: "Enter your first name",
  },
  lastName: {
    id: "patientEducation.questionnaire.lastName",
    defaultMessage: "Last Name",
  },
  lastNamePlaceholder: {
    id: "patientEducation.questionnaire.lastNamePlaceholder",
    defaultMessage: "Enter your last name",
  },
  dateOfBirth: {
    id: "patientEducation.questionnaire.dateOfBirth",
    defaultMessage: "Date of Birth",
  },
  month: {
    id: "patientEducation.questionnaire.month",
    defaultMessage: "Month",
  },
  day: {
    id: "patientEducation.questionnaire.day",
    defaultMessage: "Day",
  },
  year: {
    id: "patientEducation.questionnaire.year",
    defaultMessage: "Year",
  },
  biologicalSex: {
    id: "patientEducation.questionnaire.biologicalSex",
    defaultMessage: "Biological Sex",
  },
  female: {
    id: "patientEducation.questionnaire.female",
    defaultMessage: "Female",
  },
  male: {
    id: "patientEducation.questionnaire.male",
    defaultMessage: "Male",
  },
  nonInvasivePrenatalTesting: {
    id: "patientEducation.questionnaire.nonInvasivePrenatalTesting",
    defaultMessage: "Non-Invasive Prenatal Testing",
  },
  panoramaQuestion: {
    id: "patientEducation.questionnaire.panoramaQuestion",
    defaultMessage:
      "Are you interested in non-invasive prenatal testing to look for certain chromosome differences with your baby?",
  },
  carrierScreening: {
    id: "patientEducation.questionnaire.carrierScreening",
    defaultMessage: "Carrier Screening",
  },
  horizonQuestion: {
    id: "patientEducation.questionnaire.horizonQuestion",
    defaultMessage:
      "Are you interested in carrier screening to look at your genetic information to determine your chance of passing a genetic condition to the baby?",
  },
  yes: {
    id: "patientEducation.questionnaire.yes",
    defaultMessage: "Yes",
  },
  no: {
    id: "patientEducation.questionnaire.no",
    defaultMessage: "No",
  },
  moreInfo: {
    id: "patientEducation.questionnaire.moreInfo",
    defaultMessage: "I'd like more information",
  },
  backButton: {
    id: "patientEducation.questionnaire.backButton",
    defaultMessage: "Back",
  },
  submitButton: {
    id: "patientEducation.questionnaire.submitButton",
    defaultMessage: "Submit",
  },
  submitting: {
    id: "patientEducation.questionnaire.submitting",
    defaultMessage: "Submitting...",
  },
  // Validation error messages
  required: {
    id: "patientEducation.questionnaire.required",
    defaultMessage: "Please fill in this field",
  },
  invalidMonth: {
    id: "patientEducation.questionnaire.invalidMonth",
    defaultMessage: "Please enter a valid month (1-12)",
  },
  invalidDay: {
    id: "patientEducation.questionnaire.invalidDay",
    defaultMessage: "Please enter a valid day for the selected month",
  },
  invalidYear: {
    id: "patientEducation.questionnaire.invalidYear",
    defaultMessage: "Please enter a valid year",
  },
  ageRestriction: {
    id: "patientEducation.questionnaire.ageRestriction",
    defaultMessage: "You must be at least 18 years old",
  },
  invalidDate: {
    id: "patientEducation.questionnaire.invalidDate",
    defaultMessage: "Please enter a valid date",
  },
  futureDateError: {
    id: "patientEducation.questionnaire.futureDateError",
    defaultMessage: "Date of birth cannot be in the future",
  },
  submitError: {
    id: "patientEducation.questionnaire.submitError",
    defaultMessage: "Error submitting form. Please try again.",
  },
});

// Date validation result type
type DateValidationResult =
  | { valid: true }
  | {
      valid: false;
      errorKey:
        | "required"
        | "invalidDate"
        | "futureDateError"
        | "ageRestriction";
    };

// Validate date of birth - returns validation result
const validateDateOfBirth = (dobValue: string): DateValidationResult => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const trimmedValue = dobValue?.trim() ?? "";

  // Empty or incomplete format
  if (!trimmedValue || !dateRegex.test(trimmedValue)) {
    return { valid: false, errorKey: "required" };
  }

  const birthDate = new Date(trimmedValue);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Invalid date (e.g., Feb 30)
  if (isNaN(birthDate.getTime())) {
    return { valid: false, errorKey: "invalidDate" };
  }

  // Future date
  if (birthDate > today) {
    return { valid: false, errorKey: "futureDateError" };
  }

  // Calculate age in years
  const age =
    (today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

  if (age < 18) {
    return { valid: false, errorKey: "ageRestriction" };
  }

  if (age > 120) {
    return { valid: false, errorKey: "invalidDate" };
  }

  return { valid: true };
};

const QuestionnairePage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { publicClinicId } = usePatientEducation();

  // Easter egg: ?n=0 hides navigation (future: clinic settings will control this)
  const hideNav = searchParams.get("n") === "0";
  const { trackEvent } = usePatientEducationHeap(publicClinicId);
  const errorController = useErrorController();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    biologicalSex: BiologicalSex | null;
    panoramaInterest: InterestResponse | null;
    horizonInterest: InterestResponse | null;
  }>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    biologicalSex: null,
    panoramaInterest: null,
    horizonInterest: null,
  });

  // Track initial date of birth value to prevent infinite loop in DobInput
  const initialDateOfBirth = useState("")[0];

  const [submitForm, { loading }] = useMutation<
    SubmitPatientEducationData,
    SubmitPatientEducationVariables
  >(SUBMIT_PATIENT_EDUCATION_FORM, {
    onCompleted: () => {
      // Clear localStorage
      localStorage.removeItem("pep_panoramaPercent");
      localStorage.removeItem("pep_horizonPercent");

      if (publicClinicId) {
        const completionUrl =
          routes.patientEducationCompletionPage(publicClinicId);
        navigate(hideNav ? `${completionUrl}?n=0` : completionUrl);
      }
    },
    onError: () => {
      setSubmitError(intl.formatMessage(messages.submitError));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Capture DobInput's year error BEFORE clearing (it validates on blur)
    const existingYearError =
      errorController.getValidationError("dateOfBirthYear");

    errorController.clearErrors();

    if (!publicClinicId) return;

    let hasErrors = false;

    // Validate all fields and collect all errors (don't return early)
    const requiredError = intl.formatMessage(messages.required);

    // Validate simple required fields
    const requiredFields = [
      { field: "firstName", value: formData.firstName.trim() },
      { field: "lastName", value: formData.lastName.trim() },
      { field: "biologicalSex", value: formData.biologicalSex },
      { field: "panoramaInterest", value: formData.panoramaInterest },
      { field: "horizonInterest", value: formData.horizonInterest },
    ];

    for (const { field, value } of requiredFields) {
      if (!value) {
        errorController.setValidationError(field, requiredError);
        hasErrors = true;
      }
    }

    // Validate date of birth (special case with DobInput's year error)
    if (existingYearError) {
      errorController.setValidationError("dateOfBirthYear", existingYearError);
      hasErrors = true;
    } else {
      const dobValidation = validateDateOfBirth(formData.dateOfBirth);
      if (!dobValidation.valid) {
        errorController.setValidationError(
          "dateOfBirth",
          intl.formatMessage(messages[dobValidation.errorKey])
        );
        hasErrors = true;
      }
    }

    // If there are any errors, stop here and show all errors
    if (hasErrors) {
      return;
    }

    // Track submit button click
    trackEvent({ eventKey: HEAP_EVENTS.upp_prenataled_submit });

    // Track interest responses
    if (formData.panoramaInterest) {
      trackEvent({
        eventKey: HEAP_EVENTS.upp_prenataled_pano_interest,
        eventProperties: {
          response: formData.panoramaInterest,
        },
      });
    }

    if (formData.horizonInterest) {
      trackEvent({
        eventKey: HEAP_EVENTS.upp_prenataled_horizon_interest,
        eventProperties: {
          response: formData.horizonInterest,
        },
      });
    }

    const panoramaPercent =
      parseInt(localStorage.getItem("pep_panoramaPercent") ?? "0", 10) || 0;
    const horizonPercent =
      parseInt(localStorage.getItem("pep_horizonPercent") ?? "0", 10) || 0;

    submitForm({
      variables: {
        input: {
          clinicPublicId: publicClinicId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          biologicalSex: formData.biologicalSex,
          panoramaInterest: formData.panoramaInterest,
          horizonInterest: formData.horizonInterest,
          panoramaVideoPercent: panoramaPercent,
          horizonVideoPercent: horizonPercent,
          language: "en",
        },
      },
    });
  };

  const handleBack = () => {
    if (publicClinicId) {
      navigate(routes.patientEducationHorizonPage(publicClinicId));
    }
  };

  const handleChange = (field: string, value: string) => {
    // Clear validation error for this field
    errorController.clearValidationError(field);

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (dateValue: string) => {
    const normalizedValue = dateValue?.trim() ?? "";

    setFormData((prev) => {
      const hadPreviousValue = !!prev.dateOfBirth;

      // Validate using shared helper
      const validation = validateDateOfBirth(normalizedValue);

      if (!validation.valid) {
        // Don't show "required" error on initial empty state
        const isInitialEmpty = !normalizedValue && !hadPreviousValue;
        if (!isInitialEmpty) {
          errorController.setValidationError(
            "dateOfBirth",
            intl.formatMessage(messages[validation.errorKey])
          );
        }
      } else {
        errorController.clearValidationError("dateOfBirth");
      }

      return { ...prev, dateOfBirth: normalizedValue };
    });
  };

  // Wrapper for clearValidationError that also clears dateOfBirth when
  // DobInput clears year errors (so the "required" error from submit is cleared)
  const handleClearDobError = (field: string) => {
    errorController.clearValidationError(field);
    if (field === "dateOfBirthYear") {
      errorController.clearValidationError("dateOfBirth");
    }
  };

  return (
    <PatientEducationLoader>
      <div className="patient-education-video__container">
        <div className="patient-education-video__wrapper">
          <Progress className="patient-education-video__progress" value={100} />
          <div className="patient-education-video__content">
            {!hideNav && (
              <h2 className="patient-education__step-number">
                {intl.formatMessage(messages.stepIndicator)}
              </h2>
            )}

            <h1 className="patient-education__questionnaire-title">
              {intl.formatMessage(messages.title)}
            </h1>

            <p className="patient-education__questionnaire-description">
              {intl.formatMessage(messages.description)}
            </p>

            <ErrorProvider controller={errorController}>
              <Form
                onSubmit={handleSubmit}
                className="patient-education__form"
                noValidate
              >
                <div className="patient-education__form-section">
                  <h2 className="patient-education__form-title">
                    {intl.formatMessage(messages.yourInformation)}
                  </h2>

                  <div className="patient-education__form-row">
                    <FormField
                      label={intl.formatMessage(messages.firstName)}
                      required
                      htmlFor="firstName"
                      error={errorController.getValidationError("firstName")}
                    >
                      <Textfield
                        id="firstName"
                        name="firstName"
                        placeholder={intl.formatMessage(
                          messages.firstNamePlaceholder
                        )}
                        value={formData.firstName}
                        onChange={(e) =>
                          handleChange("firstName", e.target.value)
                        }
                        required
                        outline
                        fullWidth
                      />
                    </FormField>

                    <FormField
                      label={intl.formatMessage(messages.lastName)}
                      required
                      htmlFor="lastName"
                      error={errorController.getValidationError("lastName")}
                    >
                      <Textfield
                        id="lastName"
                        name="lastName"
                        placeholder={intl.formatMessage(
                          messages.lastNamePlaceholder
                        )}
                        value={formData.lastName}
                        onChange={(e) =>
                          handleChange("lastName", e.target.value)
                        }
                        required
                        outline
                        fullWidth
                      />
                    </FormField>
                  </div>
                  <div className="patient-education__form-row">
                    <FormField
                      label={intl.formatMessage(messages.dateOfBirth)}
                      required
                      error={
                        errorController.getValidationError("dateOfBirthYear") ??
                        errorController.getValidationError("dateOfBirth")
                      }
                    >
                      <DobInput
                        getValidationError={errorController.getValidationError}
                        setValidationError={errorController.setValidationError}
                        clearValidationError={handleClearDobError}
                        onChange={handleDateChange}
                        defaultValue={initialDateOfBirth}
                      />
                    </FormField>
                  </div>
                  <div className="patient-education__form-row">
                    <FormField
                      label={intl.formatMessage(messages.biologicalSex)}
                      required
                      error={errorController.getValidationError(
                        "biologicalSex"
                      )}
                    >
                      <fieldset>
                        <legend className="visually-hidden">
                          {intl.formatMessage(messages.biologicalSex)}
                        </legend>
                        <RadioGroup
                          name="biologicalSex"
                          value={formData.biologicalSex ?? ""}
                          onChange={(value) =>
                            handleChange("biologicalSex", value)
                          }
                        >
                          <RadioButton
                            label={intl.formatMessage(messages.female)}
                            value={BiologicalSex.FEMALE}
                          />
                          <RadioButton
                            label={intl.formatMessage(messages.male)}
                            value={BiologicalSex.MALE}
                          />
                        </RadioGroup>
                      </fieldset>
                    </FormField>
                  </div>
                </div>

                <div className="patient-education__form-section">
                  <h2 className="patient-education__form-title">
                    {intl.formatMessage(messages.nonInvasivePrenatalTesting)}
                  </h2>
                  <p className="patient-education__form-question">
                    {intl.formatMessage(messages.panoramaQuestion)}
                  </p>
                  <FormField
                    required
                    error={errorController.getValidationError(
                      "panoramaInterest"
                    )}
                  >
                    <fieldset>
                      <legend className="visually-hidden">
                        {intl.formatMessage(
                          messages.nonInvasivePrenatalTesting
                        )}
                      </legend>
                      <RadioGroup
                        name="panoramaInterest"
                        value={formData.panoramaInterest ?? ""}
                        onChange={(value) =>
                          handleChange("panoramaInterest", value)
                        }
                      >
                        <RadioButton
                          label={intl.formatMessage(messages.yes)}
                          value={InterestResponse.YES}
                        />
                        <RadioButton
                          label={intl.formatMessage(messages.no)}
                          value={InterestResponse.NO}
                        />
                        <RadioButton
                          label={intl.formatMessage(messages.moreInfo)}
                          value={InterestResponse.MORE_INFO}
                        />
                      </RadioGroup>
                    </fieldset>
                  </FormField>
                </div>

                <div className="patient-education__form-section">
                  <h2 className="patient-education__form-title">
                    {intl.formatMessage(messages.carrierScreening)}
                  </h2>
                  <p className="patient-education__form-question">
                    {intl.formatMessage(messages.horizonQuestion)}
                  </p>
                  <FormField
                    required
                    error={errorController.getValidationError(
                      "horizonInterest"
                    )}
                  >
                    <fieldset>
                      <legend className="visually-hidden">
                        {intl.formatMessage(messages.carrierScreening)}
                      </legend>
                      <RadioGroup
                        name="horizonInterest"
                        value={formData.horizonInterest ?? ""}
                        onChange={(value) =>
                          handleChange("horizonInterest", value)
                        }
                      >
                        <RadioButton
                          label={intl.formatMessage(messages.yes)}
                          value={InterestResponse.YES}
                        />
                        <RadioButton
                          label={intl.formatMessage(messages.no)}
                          value={InterestResponse.NO}
                        />
                        <RadioButton
                          label={intl.formatMessage(messages.moreInfo)}
                          value={InterestResponse.MORE_INFO}
                        />
                      </RadioGroup>
                    </fieldset>
                  </FormField>
                </div>

                {submitError && (
                  <Notification type="error">{submitError}</Notification>
                )}

                <div
                  className={`patient-education__video-actions${hideNav ? " patient-education__video-actions--single" : ""}`}
                >
                  {!hideNav && (
                    <Button type="button" onClick={handleBack}>
                      {intl.formatMessage(messages.backButton)}
                    </Button>
                  )}
                  <Button type="submit" disabled={loading} unelevated>
                    {loading
                      ? intl.formatMessage(messages.submitting)
                      : intl.formatMessage(messages.submitButton)}
                  </Button>
                </div>
              </Form>
            </ErrorProvider>
          </div>
        </div>
      </div>
    </PatientEducationLoader>
  );
};

export default QuestionnairePage;
